import flatpickr from '../../vendor/flatpickr';

const MONTHS_VISIBLE = 4;

const AR_MONTHS = [
    'يناير',
    'فبراير',
    'مارس',
    'أبريل',
    'مايو',
    'يونيو',
    'يوليو',
    'أغسطس',
    'سبتمبر',
    'أكتوبر',
    'نوفمبر',
    'ديسمبر',
];

const toMonthValue = (year, monthIndex) => `${year}-${String(monthIndex + 1).padStart(2, '0')}`;

/**
 * Inline booking calendar: a flatpickr month grid with our own month navigation.
 * Expects an `x-ref="input"` element and syncs the pick to the Livewire `form.selectedDate` property.
 *
 * The server re-seeds the calendar by dispatching `calendar-init` whenever a new service modal opens.
 *
 * @param {{ enabledDates?: string[], defaultDate?: string|null }} config
 */
export default function customCalendar({ enabledDates = [], defaultDate = null } = {}) {
    let picker = null;

    return {
        selectedDate: defaultDate,
        enabledDates,
        currentMonth: '',
        currentMonthIndex: 0,
        availableMonths: [],

        get canGoNext() {
            return this.currentMonthIndex < this.availableMonths.length - 1;
        },

        get canGoPrev() {
            return this.currentMonthIndex > 0;
        },

        init() {
            this.resetMonths();
            this.mountPicker();

            this.$wire.on('calendar-init', ({ enabledDates: dates, defaultDate: date }) => {
                this.enabledDates = dates ?? [];
                this.selectedDate = date ?? null;

                this.resetMonths();
                this.mountPicker();
            });
        },

        destroy() {
            this.unmountPicker();
        },

        resetMonths() {
            this.availableMonths = this.buildMonths();
            this.setInitialMonth();
        },

        buildMonths() {
            const today = new Date();

            return Array.from({ length: MONTHS_VISIBLE }, (_, offset) => {
                const total = today.getMonth() + offset;
                const year = today.getFullYear() + Math.floor(total / 12);
                const monthIndex = total % 12;

                return {
                    value: toMonthValue(year, monthIndex),
                    label: `${AR_MONTHS[monthIndex]} ${year}`,
                    year,
                    monthIndex,
                };
            });
        },

        setInitialMonth() {
            const selectedMonth = this.selectedDate?.split('-').slice(0, 2).join('-');
            const isSelectable = this.availableMonths.some((month) => month.value === selectedMonth);

            this.setCurrentMonth(isSelectable ? selectedMonth : (this.availableMonths[0]?.value ?? ''), {
                sync: false,
            });
        },

        mountPicker() {
            this.unmountPicker();

            this.$nextTick(() => {
                if (!this.$refs.input) {
                    return;
                }

                picker = flatpickr(this.$refs.input, this.pickerOptions());
                this.syncPickerMonth();
            });
        },

        unmountPicker() {
            picker?.destroy();
            picker = null;
        },

        pickerOptions() {
            return {
                inline: true,
                dateFormat: 'Y-m-d',
                defaultDate: this.selectedDate || null,
                minDate: 'today',
                maxDate: this.lastSelectableDate(),
                disableMobile: true,
                monthSelectorType: 'static',
                ...(this.enabledDates.length > 0 ? { enable: this.enabledDates } : {}),

                // Month navigation is ours, so hide flatpickr's own header.
                onReady: (_dates, _dateStr, instance) => {
                    const header = instance.calendarContainer.querySelector('.flatpickr-months');

                    if (header) {
                        header.style.display = 'none';
                    }
                },
                onMonthChange: (_dates, _dateStr, instance) => {
                    const month = toMonthValue(instance.currentYear, instance.currentMonth);

                    if (this.currentMonth !== month) {
                        this.setCurrentMonth(month, { sync: false });
                    }
                },
                onChange: (_dates, dateStr) => {
                    this.selectedDate = dateStr;
                    this.$wire.set('form.selectedDate', dateStr);
                    this.$dispatch('booking-date-selected', { date: dateStr });
                },
            };
        },

        lastSelectableDate() {
            const today = new Date();

            // Day 0 of the following month is the last day of the final visible month.
            return new Date(today.getFullYear(), today.getMonth() + MONTHS_VISIBLE, 0);
        },

        /**
         * The single place that changes the month, so `currentMonth` and its index never drift apart.
         */
        setCurrentMonth(value, { sync = true } = {}) {
            const index = this.availableMonths.findIndex((month) => month.value === value);

            if (index === -1) {
                return;
            }

            this.currentMonth = value;
            this.currentMonthIndex = index;

            if (sync) {
                this.syncPickerMonth();
            }
        },

        syncPickerMonth() {
            const month = this.availableMonths[this.currentMonthIndex];

            if (picker && month) {
                picker.jumpToDate(new Date(month.year, month.monthIndex, 1));
            }
        },

        /** Called by the month select, whose `x-model` sets `currentMonth` without going through setCurrentMonth. */
        changeMonth() {
            this.setCurrentMonth(this.currentMonth);
        },

        nextMonth() {
            if (this.canGoNext) {
                this.setCurrentMonth(this.availableMonths[this.currentMonthIndex + 1].value);
            }
        },

        prevMonth() {
            if (this.canGoPrev) {
                this.setCurrentMonth(this.availableMonths[this.currentMonthIndex - 1].value);
            }
        },
    };
}
